import type { PreviewPrepareOutcome } from './preview-outcome.js';
import {
  classifyPreviewPrepareCause,
  PREVIEW_PREPARE_DEADLINE_MS,
  R8_PREVIEW_COPY
} from './preview-outcome.js';
import type { Producer } from './types.js';

export type PreviewPrepareSink = {
  setCopy: (copy: string | undefined) => void;
  setUnavailable: () => void;
  setPending: (pending: boolean) => void;
};

/**
 * The single Retry command. Both surfaces that can reach it (the preview
 * overlay and the source panel's no-record overlay) issue this one, so a
 * generation bump always means the same thing to the Mediator.
 */
export const requestPreviewRetry = (producer: Producer): void => {
  producer((draft) => {
    if (draft.previewPending) return;
    draft.previewReady = false;
    draft.previewGeneration += 1;
    draft.previewCopy = undefined;
    draft.previewAvailability = 'preparing';
  });
};

/** Own token + setup; presentation timeout does not release the raw flight. */
export const runPreviewPrepare = async ({
  getToken,
  sync,
  isCurrent,
  sink,
  report,
  reference,
  deadlineMs = PREVIEW_PREPARE_DEADLINE_MS,
  setTimer = window.setTimeout.bind(window),
  clearTimer = window.clearTimeout.bind(window)
}: {
  getToken: () => Promise<string>;
  sync: (token: string) => Promise<void>;
  isCurrent: () => boolean;
  sink: PreviewPrepareSink;
  /** Safe diagnostics for the typed outcome. Copy stays fixed either way. */
  report?: ((outcome: PreviewPrepareOutcome) => void) | undefined;
  reference: string;
  deadlineMs?: number;
  setTimer?: (fn: () => void, ms: number) => number;
  clearTimer?: (id: number) => void;
}): Promise<void> => {
  const deadlineSeconds = Math.round(deadlineMs / 1000);
  sink.setPending(true);
  let operation: PreviewPrepareOutcome['operation'] = 'auth-access-token';
  const timeoutId = setTimer(() => {
    if (!isCurrent()) {
      return;
    }
    report?.({
      operation,
      transient: false,
      timedOut: true,
      copy: R8_PREVIEW_COPY(deadlineSeconds)
    });
    sink.setCopy(R8_PREVIEW_COPY(deadlineSeconds));
    sink.setUnavailable();
  }, deadlineMs);

  try {
    let token: string;
    try {
      token = await getToken();
    } catch (cause) {
      if (!isCurrent()) {
        return;
      }
      const outcome = classifyPreviewPrepareCause(cause, 'auth-access-token', reference);
      report?.(outcome);
      sink.setCopy(outcome.copy);
      sink.setUnavailable();
      return;
    }

    if (!isCurrent()) {
      return;
    }

    try {
      operation = 'preview-prepare';
      await sync(token);
    } catch (cause) {
      if (!isCurrent()) {
        return;
      }
      // Declared as the setup boundary: `sync` covers device creation as well as
      // the media token, and only the cause chain can tell which one failed.
      const outcome = classifyPreviewPrepareCause(cause, 'preview-prepare', reference);
      report?.(outcome);
      sink.setCopy(outcome.copy);
      sink.setUnavailable();
    }
  } finally {
    clearTimer(timeoutId);
    if (isCurrent()) {
      sink.setPending(false);
    }
  }
};
